import {
  backButton,
  hapticFeedback,
  init as initSdk,
  isTMA,
  miniApp,
  openLink,
  openTelegramLink,
  retrieveRawInitData,
  shareURL,
  swipeBehavior,
  themeParams,
  viewport,
} from "@tma.js/sdk-react";

import { DEV_INIT_DATA, IS_DEV } from "@/shared/config";

/**
 * Единственное место в проекте, где импортируется Telegram SDK.
 *
 * Два правила, из-за которых здесь так много try/catch:
 *  1. Клиенты Telegram (iOS / Android / Desktop / Web) поддерживают разные
 *     наборы методов. Необработанный throw на старте — белый экран вместо
 *     приложения, причём только у части пользователей.
 *  2. Приложение должно открываться и вне Telegram — иначе его нельзя
 *     отлаживать в браузере.
 */

/**
 * Возвращает результат вызова либо `undefined`, если метод недоступен или
 * бросил. Значение нужно тем вызовам, где отказ виден пользователю: шеринг
 * ссылки без него молча «ничего не делал бы» по нажатию.
 */
function attempt<T>(label: string, fn: () => T): T | undefined {
  try {
    return fn();
  } catch (error) {
    console.warn(`[telegram] ${label} недоступен`, error);
    return undefined;
  }
}

export function isInsideTelegram(): boolean {
  try {
    return isTMA();
  } catch {
    return false;
  }
}

let disposeSdk: VoidFunction | undefined;

/** @returns удалось ли поднять SDK. `false` = приложение открыто вне Telegram. */
export function initTelegram(): boolean {
  if (!isInsideTelegram()) return false;

  try {
    disposeSdk = initSdk();
  } catch (error) {
    console.warn("[telegram] init не удался", error);
    return false;
  }

  attempt("backButton.mount", () => {
    if (backButton.mount.isAvailable()) backButton.mount();
  });
  attempt("themeParams.mount", () => {
    if (themeParams.mount.isAvailable()) themeParams.mount();
  });
  attempt("miniApp.mount", () => {
    if (miniApp.mount.isAvailable()) miniApp.mount();
  });

  // Свайп вниз по умолчанию закрывает мини-апп — на скроллящихся экранах
  // жест не отличить от прокрутки, и приложение схлопывается на полпути.
  // disableVertical (Mini Apps 7.7+) оставляет закрытие только за крестиком.
  attempt("swipeBehavior.mount", () => {
    if (!swipeBehavior.mount.isAvailable()) return;
    swipeBehavior.mount();
    attempt("swipeBehavior.disableVertical", () => {
      if (swipeBehavior.disableVertical.isAvailable()) swipeBehavior.disableVertical();
    });
  });

  attempt("viewport.mount", () => {
    if (!viewport.mount.isAvailable()) return;
    void viewport
      .mount()
      .then(() => {
        // expand: без него мини-апп открывается «шторкой» на половину экрана.
        attempt("viewport.expand", () => {
          if (viewport.expand.isAvailable()) viewport.expand();
        });
        // bindCssVars даёт --tg-viewport-* и безопасные отступы: без них
        // нижняя навигация уезжает под системный индикатор на iPhone.
        attempt("viewport.bindCssVars", () => {
          if (viewport.bindCssVars.isAvailable()) viewport.bindCssVars();
        });
      })
      .catch((error: unknown) => {
        console.warn("[telegram] viewport.mount отклонён", error);
      });
  });

  // Цвета берём свои, а не из themeParams: макет тёмный и фиксированный,
  // привязка к теме клиента разошлась бы с референсом Figma.
  const base = readCssVar("--color-background-base");
  if (base) {
    attempt("miniApp.setHeaderColor", () => {
      if (miniApp.setHeaderColor.isAvailable()) miniApp.setHeaderColor(base);
    });
    attempt("miniApp.setBgColor", () => {
      if (miniApp.setBgColor.isAvailable()) miniApp.setBgColor(base);
    });
    attempt("miniApp.setBottomBarColor", () => {
      if (miniApp.setBottomBarColor.isAvailable()) miniApp.setBottomBarColor(base);
    });
  }

  return true;
}

export function disposeTelegram(): void {
  disposeSdk?.();
  disposeSdk = undefined;
}

function readCssVar(name: string): `#${string}` | undefined {
  if (typeof document === "undefined") return undefined;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value.startsWith("#") ? (value as `#${string}`) : undefined;
}

/**
 * Подписанная строка initData для `POST /auth/telegram`.
 *
 * Вне Telegram в dev возвращается строка из `VITE_TG_INIT_DATA`
 * (см. `make dev-initdata`). Байпаса на бэкенде нет и не будет: initData
 * проверяется по HMAC боттокена, поэтому dev-вход — это настоящая подпись,
 * а не отключённая аутентификация. В продовой сборке `IS_DEV` ложно и ветка
 * вырезается сборщиком.
 */
export function getRawInitData(): string | undefined {
  if (isInsideTelegram()) {
    try {
      return retrieveRawInitData();
    } catch (error) {
      console.warn("[telegram] initData не прочитан", error);
      return undefined;
    }
  }
  return IS_DEV && DEV_INIT_DATA ? DEV_INIT_DATA : undefined;
}

/* ──────────────────────────────────────────────────────────────
 * Три примитива тактильного отклика.
 *
 * Наружу они НЕ выходят: приложение обращается к отклику через шкалу
 * намерений `tactile()` из `tactile.ts` — ровно так же, как к движению
 * обращается через токены, а не через литеральные миллисекунды. Правило
 * проверяется тестом «тактильные примитивы» в src/test/quality/rules.test.ts.
 * ────────────────────────────────────────────────────────────── */

export type HapticStyle = "light" | "medium" | "heavy" | "rigid" | "soft";

/**
 * Щелчок по элементу. Telegram различает три семейства отклика, и путать их
 * значит терять тактильный язык: `impactOccurred` ощущается как удар пальца
 * о поверхность.
 */
export function haptic(style: HapticStyle = "light"): void {
  attempt("hapticFeedback.impactOccurred", () => {
    if (hapticFeedback.impactOccurred.isAvailable()) hapticFeedback.impactOccurred(style);
  });
}

export type HapticEvent = "success" | "error" | "warning";

/**
 * Отклик на исход операции, а не на нажатие: успех покупки, отказ сервера,
 * предупреждение. `notificationOccurred` ощущается как ответ системы, а не
 * как касание (FE-14).
 */
export function hapticNotification(event: HapticEvent): void {
  attempt("hapticFeedback.notificationOccurred", () => {
    if (hapticFeedback.notificationOccurred.isAvailable()) {
      hapticFeedback.notificationOccurred(event);
    }
  });
}

/**
 * Смена выбранного значения: вкладка, период графика, тумблер, чекбокс.
 *
 * Третье семейство Telegram — самое тихое из трёх, и оно единственное
 * подходит там, где пользователь не «нажал кнопку», а «перевёл переключатель»:
 * серия таких откликов подряд не превращается в дробь, в отличие от серии
 * `impactOccurred`.
 */
export function hapticSelection(): void {
  attempt("hapticFeedback.selectionChanged", () => {
    if (hapticFeedback.selectionChanged.isAvailable()) hapticFeedback.selectionChanged();
  });
}

/**
 * Поделиться ссылкой через Telegram.
 *
 * Возвращает `false`, если метод недоступен, — вызывающий экран тогда
 * оставляет пользователю копирование. Молча «ничего не произошло» после
 * нажатия быть не должно.
 */
export function shareLink(url: string, text: string): boolean {
  return attempt("shareURL", () => {
    if (!shareURL.isAvailable()) return false;
    shareURL(url, text);
    return true;
  }) ?? false;
}

/** Открыть внешнюю ссылку (правовые документы вне мини-аппа). */
export function openExternal(url: string): boolean {
  return attempt("openLink", () => {
    if (!openLink.isAvailable()) return false;
    openLink(url);
    return true;
  }) ?? false;
}

/**
 * Открыть ссылку внутри Telegram: чат, канал, профиль.
 *
 * Мини-апп при этом закрывается — так устроен сам метод, и это правильное
 * поведение для перехода в переписку: человек уходит писать сообщение, а не
 * держит два окна.
 */
export function openTelegramChat(url: string): boolean {
  return attempt("openTelegramLink", () => {
    if (!openTelegramLink.isAvailable()) return false;
    openTelegramLink(url);
    return true;
  }) ?? false;
}

/** Ссылки, которые Telegram открывает у себя, а не в браузере. */
function isTelegramLink(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "tg:") return true;
    return parsed.hostname === "t.me" || parsed.hostname === "telegram.me";
  } catch {
    return false;
  }
}

/**
 * Открыть ссылку поддержки.
 *
 * Разделение методов — не придирка: `openLink` уводит `t.me/...` во внешний
 * браузер, где вместо переписки открывается страница-визитка с кнопкой
 * «Open in Telegram». Снаружи это выглядит как «кнопка не работает» — ровно
 * та жалоба, из-за которой функция и появилась. Чат открывает только
 * `openTelegramLink`.
 *
 * Возвращает `false`, если метод недоступен: экран тогда открывает ссылку
 * обычной навигацией, а не молчит.
 */
export function openSupportLink(url: string): boolean {
  return isTelegramLink(url) ? openTelegramChat(url) : openExternal(url);
}

export function showBackButton(): void {
  attempt("backButton.show", () => {
    if (backButton.show.isAvailable()) backButton.show();
  });
}

export function hideBackButton(): void {
  attempt("backButton.hide", () => {
    if (backButton.hide.isAvailable()) backButton.hide();
  });
}

export function onBackButtonClick(listener: VoidFunction): VoidFunction {
  try {
    if (backButton.onClick.isAvailable()) return backButton.onClick(listener);
  } catch (error) {
    console.warn("[telegram] backButton.onClick недоступен", error);
  }
  return () => {};
}
